import { useTranslation } from 'next-i18next';
import {
  FC, useCallback, useMemo, useState,
} from 'react';
import Icon from 'src/common/components/IconDS22';
import debounce from 'lodash.debounce';
import LoadingSpinner from 'src/common/components/LoadingSpinner';
import getClassNames from 'src/common/utils/getClassNames';
import useFeatureFlag from 'src/common/hooks/useFeatureFlag';
import styles from './SearchField.module.scss';

export type SearchFieldProps = {
  onChange: (value: string) => void;
  /** If included, the component state will be managed externally.
    * The icon will become a loading spinner whenever `search` is not equal to the
    * typed search text (value).
    * If excluded, it will use internal state to manage the value.
    */
  search?: string;
  /** The onChange will be debounced to prevent spamming.
    * Useful for when the search is connected to an API.
    * `searching` must be included
    */
  searching?: boolean;
  delayChange?: boolean;
  theme?: 'shade';
};
const SearchField: FC<SearchFieldProps> = ({
  onChange,
  search,
  delayChange,
  theme,
  searching,
}) => {
  const [t] = useTranslation('admin-listings');
  const [value, setValue] = useState(search ?? '');
  const isNavUpdateEnabled = useFeatureFlag('pricing-v2--nav');

  const setSearchDebounced = useMemo(
    () => debounce(onChange, 600),
    [onChange],
  );

  const handleOnChange = useCallback((e) => {
    setValue(e.target.value);
    if (delayChange) {
      setSearchDebounced(e.target.value);
    } else {
      onChange(e.target.value);
    }
  }, [delayChange, setSearchDebounced, onChange]);

  return (
    <div className={getClassNames(styles, 'root', theme)}>
      <input
        value={value}
        className={styles.input}
        placeholder={t(isNavUpdateEnabled
          ? 'landing-page--search-placeholder-new'
          : 'landing-page--search-placeholder')}
        onChange={handleOnChange}
      />
      {!searching && (
        <Icon
          id="magnifying-glass"
          data-test="magnifying-glass"
        />
      )}
      {searching && <LoadingSpinner variant="xxs" />}
    </div>
  );
};

export default SearchField;
